import { useState, useEffect } from "react";
import { todayISO } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { eggProductionService } from "@/services/dailyLoggingService";
import { flockService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import DateRangeFilter, { getDefaultDateRange } from "@/components/common/DateRangeFilter";
import type { EggProduction, Flock } from "@/types/models";
import { useIsViewer, useIsOwner } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import Pagination from "@/components/ui/pagination/Pagination";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

/**
 * A blank box is NOT zero — it is "not graded", and must reach the API as null;
 * "0" is "graded, found none", a real measurement. Collapse the two and the
 * grade report tells a farm it loses nothing to grading.
 *
 * parseInt, not Number, like every other numeric on this form: Number accepts
 * "1.5" and the API rejects it with a raw unmarshal error. Garbage becomes 0
 * rather than NaN, which would serialise as null and silently mean "ungraded".
 *
 * EXPORTED so its test exercises this function rather than a copy of it.
 */
export const toGrade = (v: string): number | null =>
  v.trim() === "" ? null : parseInt(v, 10) || 0;

/**
 * Null when NOTHING was graded, so the line under the boxes stays silent on an
 * ungraded day rather than announcing "1,000 table grade of 1,000".
 */
export const gradedTotalOf = (vals: string[]): number | null =>
  vals.some((v) => v.trim() !== "")
    ? vals.reduce((a, v) => a + (parseInt(v, 10) || 0), 0)
    : null;

export default function EggCollectionPage() {
  const isViewer = useIsViewer();
  const isOwner = useIsOwner();
  const { sortConfig, requestSort } = useSortable("date", "desc");
  const [records, setRecords] = useState<EggProduction[]>([]);
  const [flocks, setFlocks] = useState<Flock[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [editing, setEditing] = useState<EggProduction | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<EggProduction | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [existingEntry, setExistingEntry] = useState<EggProduction | null>(null);

  // Filters
  const [filterFlockId, setFilterFlockId] = useState("");
  const [filterFrom, setFilterFrom] = useState(() => getDefaultDateRange().from);
  const [filterTo, setFilterTo] = useState(() => getDefaultDateRange().to);

  // Form state
  const [flockId, setFlockId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [totalTrays, setTotalTrays] = useState("");
  const [totalEggs, setTotalEggs] = useState("");
  const [brokenEggs, setBrokenEggs] = useState("0");
  // The four downgrades, held as strings so a BLANK box stays blank. "" is
  // "not graded" and "0" is "graded, none found" — collapsing them to a number
  // here would lose the distinction before it ever reached the API.
  const [cracked, setCracked] = useState("");
  const [dirty, setDirty] = useState("");
  const [doubleYolk, setDoubleYolk] = useState("");
  const [floorEggs, setFloorEggs] = useState("");
  const [notes, setNotes] = useState("");

  const loadRecords = async (fromDate?: string, toDate?: string, newOffset?: number, flockIdOverride?: string) => {
    setError("");
    try {
      const o = newOffset !== undefined ? newOffset : offset;
      const params: Record<string, string | number> = { limit, offset: o };
      const fid = flockIdOverride !== undefined ? flockIdOverride : filterFlockId;
      if (fid) params.flock_id = parseInt(fid);
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      if (f) params.from = f;
      if (t) params.to = t;
      params.sort_by = sortConfig.key;
      params.sort_order = sortConfig.direction;
      const res = await eggProductionService.list(params);
      if (Array.isArray(res)) {
        setRecords(res as unknown as EggProduction[]);
        setTotal((res as unknown as EggProduction[]).length);
      } else {
        const paginated = res as unknown as { data: EggProduction[]; meta: { total: number } };
        setRecords(paginated.data ?? []);
        setTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadFlocks = async () => {
    try {
      const data = await flockService.list();
      setFlocks((data as Flock[]).filter((f) => f.status === "active"));
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    loadFlocks();
    loadRecords();
  }, []);

  useEffect(() => {
    setLoading(true);
    loadRecords();
  }, [sortConfig]);

  // isEditing is passed explicitly rather than read from the `editing` state:
  // openCreate calls this right after setEditing(null), and that state update
  // hasn't applied yet in the same tick — reading the stale `editing` here
  // suppressed the "an entry already exists" warning on the first create after
  // any edit.
  const checkExistingEntry = async (fId: string, d: string, isEditing: boolean) => {
    if (!fId || !d || isEditing) {
      setExistingEntry(null);
      return;
    }
    try {
      const res = await eggProductionService.checkExisting(parseInt(fId), d);
      if (res.exists && res.record) {
        setExistingEntry(res.record);
      } else {
        setExistingEntry(null);
      }
    } catch {
      setExistingEntry(null);
    }
  };

  const switchToUpdate = () => {
    if (!existingEntry) return;
    setEditing(existingEntry);
    setTotalTrays(existingEntry.total_trays.toString());
    setTotalEggs(existingEntry.total_eggs.toString());
    setBrokenEggs(existingEntry.broken_eggs.toString());
    setGrades(existingEntry);
    setNotes(existingEntry.notes || "");
    setExistingEntry(null);
  };

  // Only birds in a layer shed lay. The server refuses eggs for a flock in a
  // brooder; offering it here would let somebody type a day's count first.
  // When EDITING, the row's own flock stays offered even if it is in a brooder:
  // the server allows correcting a row on its flock, only not moving one there.
  const layingFlocks = flocks.filter(
    (f) => f.shed_type !== "brooder" || (editing !== null && f.id === editing.flock_id),
  );

  const openCreate = () => {
    setEditing(null);
    setExistingEntry(null);
    const defaultFlockId = layingFlocks.length > 0 ? layingFlocks[0].id.toString() : "";
    const defaultDate = todayISO();
    setFlockId(defaultFlockId);
    setDate(defaultDate);
    setTotalTrays("");
    setTotalEggs("");
    setBrokenEggs("0");
    // Blank, not "0": a fresh form has not graded anything, and pre-filling
    // zeros would record "graded, none found" for every day somebody opens
    // the form and saves without looking at a tray.
    setCracked("");
    setDirty("");
    setDoubleYolk("");
    setFloorEggs("");
    setNotes("");
    setError("");
    setModalOpen(true);
    if (defaultFlockId) {
      checkExistingEntry(defaultFlockId, defaultDate, false);
    }
  };

  const openEdit = (rec: EggProduction) => {
    setEditing(rec);
    setExistingEntry(null);
    setFlockId(rec.flock_id.toString());
    setDate(new Date(rec.date).toISOString().split("T")[0]);
    setTotalTrays(rec.total_trays.toString());
    setTotalEggs(rec.total_eggs.toString());
    setBrokenEggs(rec.broken_eggs.toString());
    setGrades(rec);
    setNotes(rec.notes || "");
    setError("");
    setModalOpen(true);
  };

  const handleTraysChange = (value: string) => {
    setTotalTrays(value);
    if (value) {
      setTotalEggs((parseInt(value) * 30).toString());
    } else {
      setTotalEggs("");
    }
  };

  // null -> "" and 0 -> "0", so reopening an edit shows exactly what was
  // stored: a blank box for a day nobody graded, a zero for a day graded clean.
  const setGrades = (rec: EggProduction) => {
    setCracked(rec.eggs_cracked?.toString() ?? "");
    setDirty(rec.eggs_dirty?.toString() ?? "");
    setDoubleYolk(rec.eggs_double_yolk?.toString() ?? "");
    setFloorEggs(rec.eggs_floor?.toString() ?? "");
  };

  const gradedTotal = gradedTotalOf([cracked, dirty, doubleYolk, floorEggs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const data = {
        flock_id: parseInt(flockId),
        date,
        total_trays: parseInt(totalTrays) || 0,
        total_eggs: parseInt(totalEggs) || 0,
        broken_eggs: parseInt(brokenEggs) || 0,
        eggs_cracked: toGrade(cracked),
        eggs_dirty: toGrade(dirty),
        eggs_double_yolk: toGrade(doubleYolk),
        eggs_floor: toGrade(floorEggs),
        notes: notes || undefined,
      };
      if (editing) {
        await eggProductionService.update(editing.id, data);
      } else {
        await eggProductionService.create(data);
      }
      setModalOpen(false);
      setLoading(true);
      await loadRecords();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await eggProductionService.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      setLoading(true);
      await loadRecords();
    } catch (err) {
      setError(handleApiError(err));
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleFormDateChange = (value: string) => {
    setDate(value);
    checkExistingEntry(flockId, value, editing !== null);
  };

  const handleFormFlockChange = (value: string) => {
    setFlockId(value);
    checkExistingEntry(value, date, editing !== null);
  };

  const handleDateRangeApply = (from: string, to: string) => {
    setFilterFrom(from);
    setFilterTo(to);
    setOffset(0);
    setLoading(true);
    loadRecords(from, to, 0);
  };

  const handleFlockChange = (value: string) => {
    setFilterFlockId(value);
    setOffset(0);
    setLoading(true);
    loadRecords(undefined, undefined, 0, value);
  };

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    loadRecords(undefined, undefined, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    loadRecords(undefined, undefined, newOffset);
  };

  return (
    <>
      <PageMeta title="Egg Collection | Poultry Book" description="Daily egg production logging" />
      <PageBreadcrumb pageTitle="Egg Collection" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Egg Production {total > 0 && <span className="text-sm font-normal text-gray-500">({total})</span>}
          </h3>
          {!isViewer && (
            <Button size="sm" onClick={openCreate}>
              Log Eggs
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-3 px-5 pb-4 sm:flex-row sm:flex-wrap sm:items-center sm:px-6">
          <select
            value={filterFlockId}
            onChange={(e) => handleFlockChange(e.target.value)}
            className={`${selectClass} sm:max-w-[200px]`}
          >
            <option value="">All Flocks</option>
            {flocks.map((f) => (
              <option key={f.id} value={f.id}>
                {f.batch_name}
              </option>
            ))}
          </select>
          <DateRangeFilter onApply={handleDateRangeApply} />
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : records.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No egg production in the selected period.
            </div>
          ) : records.length === 0 ? null : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Flock/Shed" sortKey="flock" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Trays
                  </TableCell>
                  <SortableHeader label="Eggs" sortKey="total_eggs" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Broken" sortKey="broken_eggs" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Notes
                  </TableCell>
                  {!isViewer && (
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((rec) => (
                  <TableRow key={rec.id} className="border-t border-gray-100 dark:border-gray-800">
                    <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                      {new Date(rec.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {rec.flock_name}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right text-gray-800 dark:text-white/90">
                      {rec.total_trays.toLocaleString()}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                      {rec.total_eggs.toLocaleString()}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right text-error-500">
                      {rec.broken_eggs > 0 ? rec.broken_eggs : "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {rec.notes || "-"}
                    </TableCell>
                    {!isViewer && (
                      <TableCell className="px-5 py-3 text-right">
                        <button
                          onClick={() => openEdit(rec)}
                          className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                        >
                          Edit
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => setDeleteConfirm(rec)}
                            className="ml-3 text-sm text-error-500 hover:text-error-600 dark:text-error-400"
                          >
                            Delete
                          </button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
      </div>

      {/* Create Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-md p-5 sm:p-8 mx-4">
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Egg Collection" : "Log Egg Collection"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="date">Date *</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => handleFormDateChange(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="flockId">Flock *</Label>
            <select
              id="flockId"
              value={flockId}
              onChange={(e) => handleFormFlockChange(e.target.value)}
              required
              className={selectClass}
            >
              <option value="">Select flock</option>
              {layingFlocks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.batch_name} ({f.shed_code})
                </option>
              ))}
            </select>
          </div>

          {existingEntry && !editing && (
            <div className="rounded-lg border border-warning-300 bg-warning-50 p-3 dark:border-warning-500/30 dark:bg-warning-500/10">
              <p className="text-sm font-medium text-warning-700 dark:text-warning-400">
                An entry already exists for this flock on {new Date(existingEntry.date).toLocaleDateString()} ({existingEntry.total_eggs.toLocaleString()} eggs).
              </p>
              <button
                type="button"
                onClick={switchToUpdate}
                className="mt-2 text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
              >
                Update existing entry instead
              </button>
            </div>
          )}

          <div>
            <Label htmlFor="totalTrays">Total Trays *</Label>
            <Input
              id="totalTrays"
              type="number"
              value={totalTrays}
              onChange={(e) => handleTraysChange(e.target.value)}
              placeholder="e.g. 100"
              required
              min={0}
            />
          </div>

          <div>
            <Label htmlFor="totalEggs">Total Eggs</Label>
            <Input
              id="totalEggs"
              type="number"
              value={totalEggs}
              onChange={(e) => setTotalEggs(e.target.value)}
              placeholder="Auto-calculated (trays x 30)"
              min={0}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Auto-calculated as trays x 30. Edit if different.
            </p>
          </div>

          <div>
            <Label htmlFor="brokenEggs">Broken Eggs</Label>
            <Input
              id="brokenEggs"
              type="number"
              value={brokenEggs}
              onChange={(e) => setBrokenEggs(e.target.value)}
              placeholder="0"
              min={0}
            />
            {/* Two boxes on this form count damaged eggs and they are NOT the
                same number. This one feeds the Egg Stock report's Breakage
                column; Cracked below is a grade, and drives what the mix is
                worth. Reconciling them would move a figure on a shipped
                screen, so they are labelled rather than merged. */}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Eggs lost to breakage — shown on the Egg Stock report. Not the same as the Cracked
              grade below.
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Grading (optional)</p>
            <p className="mt-0.5 mb-3 text-xs text-gray-500 dark:text-gray-400">
              Count the eggs that will not fetch the table rate. Whatever is left is table grade.
              Leave blank if nobody graded — a blank is not a zero. Cracked here is a grade, separate
              from Breakage above.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {(
                [
                  ["cracked", "Cracked", cracked, setCracked],
                  ["dirty", "Dirty", dirty, setDirty],
                  ["doubleYolk", "Double yolk", doubleYolk, setDoubleYolk],
                  ["floorEggs", "Floor", floorEggs, setFloorEggs],
                ] as const
              ).map(([id, label, value, set]) => (
                <div key={id}>
                  <Label htmlFor={id}>{label}</Label>
                  <Input
                    id={id}
                    type="number"
                    value={value}
                    onChange={(e) => set(e.target.value)}
                    placeholder="—"
                    min={0}
                  />
                </div>
              ))}
            </div>
            {gradedTotal !== null && (
              <p
                className={`mt-2 text-xs ${
                  gradedTotal > (parseInt(totalEggs) || 0)
                    ? "text-error-600 dark:text-error-400"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {gradedTotal > (parseInt(totalEggs) || 0)
                  ? `${gradedTotal} graded is more than the day's ${parseInt(totalEggs) || 0} eggs — table would be negative.`
                  : `${(parseInt(totalEggs) || 0) - gradedTotal} table grade of ${parseInt(totalEggs) || 0}.`}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !flockId || !totalTrays}>
              {saving ? "Saving..." : editing ? "Update" : "Log Eggs"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        title="Delete Entry"
        message={
          <>
            Are you sure you want to delete the egg production entry for{" "}
            <span className="font-medium">{deleteConfirm && new Date(deleteConfirm.date).toLocaleDateString()}</span>{" "}
            ({deleteConfirm?.total_eggs.toLocaleString()} eggs)? This action cannot be undone.
          </>
        }
        busy={deleting}
        busyLabel="Deleting…"
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm(null)}
      />
    </>
  );
}
